import React, { Fragment, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Form, FormGroup, Label, Input, Table, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSuppliersQuery, useGetStoresQuery, useCreatePurchaseOrderMutation, useUpdatePurchaseOrderMutation, useGetPurchaseOrderByIdQuery } from '../../../../store/services/inventoryApi';
import { Trash } from 'react-feather';
import ProductSearch from '../../../Common/ProductSearch';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
    supplierId: yup.string().required('Supplier is required'),
    storeId: yup.string().required('Store is required'),
    expectedDate: yup.date().required('Expected Date is required').typeError('Invalid Date'),
    notes: yup.string(),
    items: yup.array().of(
        yup.object().shape({
            productVariantId: yup.string().required(),
            productName: yup.string(),
            sku: yup.string(),
            orderedQty: yup.number().required().min(1, 'Qty must be at least 1').typeError('Qty must be a number'),
            unitCost: yup.number().required().min(0, 'Cost must be positive').typeError('Cost must be a number')
        })
    ).min(1, 'At least one item is required')
});

const PurchaseOrderForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    // API Hooks
    const { data: suppliersData } = useGetSuppliersQuery();
    const { data: storesData } = useGetStoresQuery();
    const [createPO, { isLoading: isCreating }] = useCreatePurchaseOrderMutation();
    const [updatePO, { isLoading: isUpdating }] = useUpdatePurchaseOrderMutation();
    const { data: poData, isLoading: isLoadingPO } = useGetPurchaseOrderByIdQuery(id, { skip: !isEdit });

    // Local State


    // Form Setup
    const { control, register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            supplierId: '',
            storeId: '',
            expectedDate: new Date().toISOString().split('T')[0],
            notes: '',
            items: []
        }
    });

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: "items"
    });

    // Populate Form in Edit Mode
    useEffect(() => {
        if (isEdit && poData && poData.data) { // Unwrap if wrapped or direct
            const po = poData.data || poData;
            reset({
                supplierId: po.supplierId?._id || po.supplierId,
                storeId: po.storeId?._id || po.storeId,
                expectedDate: po.expectedDate ? new Date(po.expectedDate).toISOString().split('T')[0] : '',
                notes: po.notes,
                items: po.items.map(item => ({
                    productVariantId: item.productVariantId,
                    productName: item.productName,
                    sku: item.sku,
                    orderedQty: item.orderedQty,
                    unitCost: item.unitCost
                }))
            });
        }
    }, [isEdit, poData, reset]);

    const items = watch('items');



    const handleAddItem = (variant) => {
        const existingIndex = items.findIndex(i => i.productVariantId === variant.productVariantId);
        if (existingIndex >= 0) {
            toast.info("Item already added. Adjust quantity in the list.");
        } else {
            append({
                productVariantId: variant.productVariantId,
                productName: `${variant.productName} - ${variant.variantName || variant.sku}`,
                sku: variant.sku,
                orderedQty: 1,
                unitCost: variant.storePricing?.[0]?.sellingPrice || 0
            });
        }
    };

    const onSubmit = async (data) => {
        // Validate active supplier
        const selectedSupplier = suppliersData?.data?.find(s => s._id === data.supplierId);
        if (selectedSupplier && selectedSupplier.status === 'Inactive') {
            toast.error('Cannot save purchase order for an inactive supplier');
            return;
        }

        try {
            if (isEdit) {
                await updatePO({ id, ...data }).unwrap();
                toast.success('Purchase Order updated successfully');
            } else {
                await createPO(data).unwrap();
                toast.success('Purchase Order created successfully');
            }
            navigate('/inventory/purchase-orders');
        } catch (error) {
            toast.error(error?.data?.message || 'Failed to save Purchase Order');
            console.error(error);
        }
    };

    // Derived Totals
    const grandTotal = items?.reduce((sum, item) => sum + (Number(item.orderedQty || 0) * Number(item.unitCost || 0)), 0) || 0;

    return (
        <Fragment>
            <Breadcrumbs mainTitle={isEdit ? "Edit Purchase Order" : "Create Purchase Order"} parent="Inventory" title={isEdit ? "Edit PO" : "Create PO"} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>{isEdit ? "Update Purchase Order Details" : "New Purchase Order"}</h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Supplier <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="supplierId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input type="select" {...field} invalid={!!errors.supplierId}>
                                                            <option value="">Select Supplier</option>
                                                            {suppliersData?.data?.filter(s => s.status !== 'Inactive' || s._id === field.value).map(s => (
                                                                <option key={s._id} value={s._id}>
                                                                    {s.name} {s.code ? `(${s.code})` : ''} {s.status === 'Inactive' ? '(Inactive)' : ''}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                />
                                                {errors.supplierId && <span className="text-danger">{errors.supplierId.message}</span>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Destination Store <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="storeId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input type="select" {...field} invalid={!!errors.storeId}>
                                                            <option value="">Select Store</option>
                                                            {storesData?.data?.map(w => (
                                                                <option key={w._id} value={w._id}>{w.storeName} {w.storeNumber ? `(${w.storeNumber})` : ''}</option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                />
                                                {errors.storeId && <span className="text-danger">{errors.storeId.message}</span>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Expected Date <span className="text-danger">*</span></Label>
                                                <input className="form-control" type="date" {...register('expectedDate')} />
                                                {errors.expectedDate && <span className="text-danger">{errors.expectedDate.message}</span>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Notes</Label>
                                                <textarea className="form-control" {...register('notes')} />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <hr />

                                    <h5>Order Items</h5>

                                    {(!isEdit || (poData?.data?.status === 'Draft' || poData?.status === 'Draft')) && (
                                        <ProductSearch
                                            onProductSelect={handleAddItem}
                                            placeholder="Search Products to Add..."
                                            selectedIds={fields.map(f => f.productVariantId)}
                                            storeId={watch('storeId')}
                                        />
                                    )}

                                    <Table hover responsive bordered>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40%' }}>Product</th>
                                                <th style={{ width: '15%' }}>SKU</th>
                                                <th style={{ width: '15%' }}>Quantity</th>
                                                <th style={{ width: '15%' }}>Unit Cost</th>
                                                <th style={{ width: '10%' }}>Total</th>
                                                <th style={{ width: '5%' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fields.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>{item.productName}</td>
                                                    <td>{item.sku}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className={`form-control form-control-sm ${errors.items?.[index]?.orderedQty ? 'is-invalid' : ''}`}
                                                            {...register(`items.${index}.orderedQty`, { valueAsNumber: true })}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className={`form-control form-control-sm ${errors.items?.[index]?.unitCost ? 'is-invalid' : ''}`}
                                                            {...register(`items.${index}.unitCost`)}
                                                        />
                                                    </td>
                                                    <td>
                                                        ${((watch(`items.${index}.orderedQty`) || 0) * (watch(`items.${index}.unitCost`) || 0)).toFixed(2)}
                                                    </td>
                                                    <td>
                                                        <Button color="danger" size="xs" onClick={() => remove(index)}>
                                                            <Trash size={14} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {fields.length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted">No items added to this order.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="4" className="text-end"><strong>Grand Total:</strong></td>
                                                <td><strong>${grandTotal.toFixed(2)}</strong></td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                    {errors.items && <p className="text-danger">{errors.items.message}</p>}

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <Button color="light" onClick={() => navigate('/inventory/purchase-orders')}>Cancel</Button>
                                        <Button color="primary" type="submit" disabled={isCreating || isUpdating}>
                                            {(isCreating || isUpdating) ? <Spinner size="sm" /> : (isEdit ? 'Update PO' : 'Create PO')}
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default PurchaseOrderForm;
